// Constants
import { ETagVersionRollbackError } from "@ms/uno-constants/lib/local/ErrorConstants";
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { Table } from "@ms/uno-entitycore/lib/local/table/Table";
// Models
import { DeltaType } from "@ms/planner-models/lib/local/client/deltaSync/Enumerations";
// Utilities
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import { collection, status } from "@ms/uno-utilities/lib/local/storage/TableNameUtilities";
import { Temporal } from "temporal-polyfill-lite";
import { getStateBasedOnResponse, isStaleDeltaTokenStatus, updateStatus } from "../utilities/StatusUtilities";
/**
 * An abstract slice class that provides basic functionality for managing a collection of items.
 * It includes methods for fetching, creating, updating, and deleting items, as well as managing
 * their statuses and collections.
 *
 * @template TRow - The type of the items stored in the slice.
 * @template TInput - The type of the items provided to the slice for operations.
 */ export class AbstractSlice {
    table() {
        return this.itemTable;
    }
    handleFetchStarted(id) {
        return updateStatus(this.statusTable, id, {
            isFetching: true
        });
    }
    handleFetchFailed(id, response) {
        return updateStatus(this.statusTable, id, {
            isFetching: false,
            state: response ? getStateBasedOnResponse(response) ?? StoreEntityState.UnableToFetch : StoreEntityState.UnableToFetch
        });
    }
    handleFetchSucceeded(id, item, overrideVersionCheck) {
        let sliceChanged = false;
        sliceChanged = this.upsertEntityAndStatus(id, item, overrideVersionCheck) || sliceChanged;
        sliceChanged = updateStatus(this.statusTable, id, {
            isFetching: false
        }) || sliceChanged;
        return sliceChanged;
    }
    handleCreateStarted(localId, localItem) {
        return this.upsertEntityAndStatus(localId, localItem);
    }
    handleCreateFailed(localId) {
        const sliceCopy = this.itemTable.get(localId);
        if (sliceCopy == null) {
            return false;
        }
        return this.deleteEntityAndStatus(localId);
    }
    handleCreateSucceeded(localId, createdItem, serverId) {
        const sliceCopy = this.itemTable.get(localId);
        if (sliceCopy == null) {
            return false;
        }
        // Update the entity with the server version
        this.handleInput(localId, createdItem);
        // If the created item ID is different from the local ID, we need to re-key the entity
        if (serverId !== localId) {
            this.itemTable.reKey(localId, serverId);
            this.deleteEntityAndStatus(localId);
        }
        updateStatus(this.statusTable, serverId, {
            state: StoreEntityState.Present
        });
        return true;
    }
    handleDeleteStarted(id) {
        const sliceCopy = this.itemTable.get(id);
        const sliceStatus = this.statusTable.get(id);
        if (sliceCopy == null || sliceStatus?.state === StoreEntityState.SoftDeleted) {
            return false;
        }
        updateStatus(this.statusTable, id, {
            state: StoreEntityState.SoftDeleted
        });
        return true;
    }
    handleDeleteFailed(id, response, errorCode) {
        const sliceCopy = this.itemTable.get(id);
        if (sliceCopy == null) {
            // If the entity does not exist, we don't need to do anything.
            return false;
        }
        let sliceChanged = false;
        const sliceStatus = this.statusTable.get(id);
        if (sliceStatus?.state === StoreEntityState.SoftDeleted) {
            // If the entity is already soft deleted, we can revert the soft delete.
            updateStatus(this.statusTable, id, {
                state: StoreEntityState.Present
            });
            sliceChanged = true;
        }
        // ETagVersionRollback (HTTP 412) means the service reset its etag scheme, so the local
        // baseline is no longer trustworthy. Mark the entity Dirty so the next fetch overwrites the
        // baseline unconditionally (see handleInput); the Dirty bit clears on the next successful
        // upsert (see upsertEntityAndStatus). Gated by EnableDirtyBitCheck. Otherwise, map the
        // response to a new state.
        const isEtagRollback = this.configProvider().flights.EnableDirtyBitCheck && errorCode === ETagVersionRollbackError;
        const newState = isEtagRollback ? StoreEntityState.Dirty : response ? getStateBasedOnResponse(response) : undefined;
        if (newState) {
            sliceChanged = updateStatus(this.statusTable, id, {
                state: newState
            }) || sliceChanged;
        }
        return sliceChanged;
    }
    handleDeleteSucceeded(id) {
        const sliceCopy = this.itemTable.get(id);
        if (sliceCopy == null) {
            // If the entity does not exist, we cannot delete it.
            return false;
        }
        this.deleteEntityAndStatus(id);
        return true;
    }
    handleUndeleteStarted(id, localItem) {
        const sliceCopy = this.itemTable.get(id);
        const sliceStatus = this.statusTable.get(id);
        if (sliceCopy != null || sliceStatus?.state === StoreEntityState.Present) {
            return false;
        }
        this.upsertEntityAndStatus(id, localItem);
        return true;
    }
    handleUndeleteFailed(id) {
        const sliceCopy = this.itemTable.get(id);
        if (sliceCopy == null) {
            // If the entity does not exist, we don't need to do anything.
            return false;
        }
        let sliceChanged = false;
        const sliceStatus = this.statusTable.get(id);
        if (sliceStatus?.state === StoreEntityState.Present) {
            this.deleteEntityAndStatus(id);
            sliceChanged = true;
        }
        return sliceChanged;
    }
    handleUndeleteSucceeded(id, restoredItem) {
        return this.upsertEntityAndStatus(id, restoredItem);
    }
    handleDeltaSyncStarted(collection) {
        return updateStatus(this.getCollectionStatusTable(collection.name), collection.value, {
            isFetching: true
        });
    }
    handleDeltaSyncSucceeded(collectionDescriptor, items, deltaLink) {
        let sliceChanged = false;
        for (const [itemId, item] of Object.entries(items)){
            sliceChanged = this.handleDeltaSyncItem(itemId, item) || sliceChanged;
        }
        const collectionStatusTable = this.getCollectionStatusTable(collectionDescriptor.name);
        const currentDeltaState = collectionStatusTable.get(collectionDescriptor.value)?.deltaSyncState;
        const emptyStreak = Object.keys(items).length === 0 ? (currentDeltaState?.emptyStreak ?? 0) + 1 : 0;
        const backoffErrorStreak = 0; // Reset backoff factor on successful delta sync
        sliceChanged = updateStatus(collectionStatusTable, collectionDescriptor.value, {
            isFetching: false,
            state: StoreEntityState.Present,
            deltaSyncState: {
                link: deltaLink,
                backoffErrorStreak,
                emptyStreak
            }
        }) || sliceChanged;
        return sliceChanged;
    }
    handleDeltaSyncFailed(collection, response, errorCode) {
        const collectionStatusTable = this.getCollectionStatusTable(collection.name);
        const existingDeltaSyncState = collectionStatusTable.get(collection.value)?.deltaSyncState;
        const status = response?.status;
        // Create a new deltaSyncState object to avoid mutating the existing one
        let link = existingDeltaSyncState?.link ?? null;
        let backoffErrorStreak = existingDeltaSyncState?.backoffErrorStreak ?? 0;
        // Token timed out
        if (isStaleDeltaTokenStatus(status)) {
            link = null;
        }
        // For certain status codes we increase the failure streak to backoff appropriately
        const statusCodesRequiringBackoff = [
            HttpStatus.TooManyRequests,
            HttpStatus.BadGateway,
            HttpStatus.ServiceUnavailable,
            HttpStatus.GatewayTimeout
        ];
        if (status && statusCodesRequiringBackoff.includes(status)) {
            backoffErrorStreak += 1;
        }
        // HTTP 410 Gone means the service rolled its etag scheme — local cached data for the
        // entities in this collection cannot be trusted. Mark each entity Dirty so the next
        // fetch bypasses any client-side version / etag comparison for those entities. The
        // Dirty bit on an entity is cleared automatically by the next successful upsert
        // (see upsertEntityAndStatus). The collection itself follows the normal failure mapping.
        const isEtagRollback = this.configProvider().flights.EnableDirtyBitCheck && errorCode === ETagVersionRollbackError;
        if (isEtagRollback) {
            const itemIds = this.itemTable.index(collection.name).partition(collection.value).itemIds();
            for (const itemId of itemIds){
                updateStatus(this.statusTable, itemId, {
                    state: StoreEntityState.Dirty
                });
            }
        }
        return updateStatus(collectionStatusTable, collection.value, {
            isFetching: false,
            state: isEtagRollback ? StoreEntityState.Dirty : response ? getStateBasedOnResponse(response) ?? StoreEntityState.UnableToFetch : StoreEntityState.UnableToFetch,
            deltaSyncState: {
                link,
                backoffErrorStreak,
                emptyStreak: existingDeltaSyncState?.emptyStreak ?? 0
            }
        });
    }
    handleCollectionFetchStarted(collection) {
        return updateStatus(this.getCollectionStatusTable(collection.name), collection.value, {
            isFetching: true
        });
    }
    handleCollectionFetchFailed(collection, response) {
        return updateStatus(this.getCollectionStatusTable(collection.name), collection.value, {
            isFetching: false,
            state: response ? getStateBasedOnResponse(response) ?? StoreEntityState.UnableToFetch : StoreEntityState.UnableToFetch
        });
    }
    handleCollectionFetchSucceeded(collection, isLastPage, itemsInCurrentPage, accumulatedItemIds, deltaLink, overrideVersionCheck) {
        /**
         * Execute a batch operation to handle the collection fetch
         * NOTE: Batching is crucial to avoid  internal table updates
         * for each item individually, which can be expensive.
         */ const allIds = new Set(accumulatedItemIds);
        let sliceChanged = this.table().executeBatch(()=>{
            // Add entities from current page to the slice
            for (const [itemId, item] of Object.entries(itemsInCurrentPage)){
                // When the service signaled an etag scheme rollback, override the version check so the
                // fetched value replaces the local baseline unconditionally (see handleInput).
                this.handleFetchSucceeded(itemId, item, overrideVersionCheck);
                // Ensure that the items in the last page are part of the accumulated item ids
                allIds.add(itemId);
            }
            if (isLastPage) {
                const indexedItemIds = this.itemTable.index(collection.name).partition(collection.value).itemIds();
                this.deleteExtraneousEntities(allIds, indexedItemIds);
            }
        });
        // If deltaLink is provided, set delta sync state to default one with the new deltaLink
        const deltaSyncState = deltaLink ? {
            link: deltaLink,
            backoffErrorStreak: 0,
            emptyStreak: 0
        } : undefined;
        sliceChanged = updateStatus(this.getCollectionStatusTable(collection.name), collection.value, {
            isFetching: false,
            state: isLastPage ? StoreEntityState.Present : undefined,
            deltaSyncState: deltaSyncState
        }) || sliceChanged;
        return sliceChanged;
    }
    /**
     * Delete extraneous entity from a collection index
     * It's possible that an entity that is indexed to a particular collection is no longer a part of that collection.
     * This ensures that we clean them up when we have the latest info for that collection.
     * @param accumulatedItemIds accumulated entity ids across all pages of response
     * @param indexedItemIds items currently indexed for this collection
     */ deleteExtraneousEntities(accumulatedItemIds, indexedItemIds) {
        let sliceChanged = false;
        for (const itemId of indexedItemIds){
            // We need to preserve following entities in the final index-
            //  1. The latest accumulated set of entities via the service
            //  2. Local temporary entities as they are awaiting in-flight requests
            if (!accumulatedItemIds.has(itemId) && !LocalIdGenerator.isLocalTempId(itemId)) {
                this.handleDeleteSucceeded(itemId);
                sliceChanged = true;
            }
        }
        return sliceChanged;
    }
    /**
     * Upsert an entity and its status
     * @param id Id of the entity
     * @param item Entity to add
     * @param overrideVersionCheck When true, bypass the client-side isNewer version comparison. Honored only
     * by version-aware slices (see UpdatableSlice); the base implementation performs no version check.
     */ upsertEntityAndStatus(id, item, overrideVersionCheck) {
        const currentEntity = this.itemTable.get(id);
        if (currentEntity != null && isEqual(currentEntity, item)) {
            // If the entity already exists and with same value, do not add it again.
            return false;
        }
        this.handleInput(id, item, overrideVersionCheck);
        this.statusTable.set(id, {
            isFetching: false,
            state: StoreEntityState.Present,
            timestamp: Temporal.Now.instant()
        });
        return true;
    }
    /**
     * Delete an entity from the store tables
     * @param id Id of the entity
     */ deleteEntityAndStatus(id) {
        this.itemTable.delete(id);
        this.statusTable.delete(id);
        return true;
    }
    /**
     * Get status tables from this slice using centralized table name mappings.
     */ statusTables() {
        return {
            [status(this.name)]: this.statusTable,
            ...Object.fromEntries(Object.entries(this.collectionStatusTables).map(([collectionName, table])=>[
                    collection(this.name, collectionName),
                    table
                ]))
        };
    }
    /**
     * Ensure that the collection status table exists for a given collection name.
     * @param collectionName Name of the collection
     */ getCollectionStatusTable(collectionName) {
        if (!this.collectionStatusTables[collectionName]) {
            this.collectionStatusTables[collectionName] = new Table(collection(this.name, collectionName));
        }
        return this.collectionStatusTables[collectionName];
    }
    /**
     * Handles a single delta sync item based on its type.
     * This method can be overridden in derived classes to support additional delta types.
     * @param itemId The ID of the item
     * @param item The delta entity containing the operation type and payload
     * @returns True if the slice was changed, false otherwise
     */ handleDeltaSyncItem(itemId, item) {
        switch(item.type){
            case DeltaType.Create:
                return this.handleFetchSucceeded(itemId, item.entity);
            case DeltaType.Delete:
                return this.handleDeleteSucceeded(itemId);
            default:
                // unsupported delta types
                throw new Error("Unsupported delta type");
        }
    }
    /**
     * Creates an instance of Slice
     * @param name Name of the slice
     * @param indexDefinitions Index definitions for collections in the slice
     * @param configProvider Provider for app configuration
     */ constructor(name, indexDefinitions, configProvider){
        this.name = name;
        this.indexDefinitions = indexDefinitions;
        this.configProvider = configProvider;
        /** Table that stores all entities of this slice */ this.itemTable = this.createTable();
        /** Table that stores the status of all entities in this slice */ this.statusTable = new Table(status(this.name));
        /** Collection status tables for each index defined in the slice */ this.collectionStatusTables = {};
        for (const indexName of Object.keys(indexDefinitions)){
            this.collectionStatusTables[indexName] = this.getCollectionStatusTable(indexName);
        }
    }
}
